import { Accordion, Box, Button, Container, Group, Stack, Text, Title } from "@mantine/core";
import { IconBrandWhatsapp } from "@tabler/icons-react";
import { WHATSAPP_URL } from "$/data/links";

type Faq = { q: string; a: string };

const faqs: Faq[] = [
  {
    q: "Vou ter custos mensais com o app?",
    a: "Não obrigatórios. Você compra uma vez e o Verdana é seu pra sempre. Existe um plano de manutenção opcional de R$19/mês, com visitas técnicas sem custo adicional, suporte por WhatsApp prioritário, backup na nuvem e correção de bugs também prioritária. Se quiser continuar sem esse plano, o sistema continua rodando normalmente.",
  },
  {
    q: "Quanto custa cada funcionalidade adicional?",
    a: "Cada adicional tem um valor fixo (de R$19 a R$99), pago uma única vez. Os valores estão na seção de preço, e qualquer dúvida é só chamar no WhatsApp.",
  },
  {
    q: "Comprando uma vez, posso instalar em quantos computadores eu quiser?",
    a: "Não. A licença vale para um computador. Se você precisar instalar em mais máquinas, é necessário comprar uma licença adicional por máquina.",
  },
  {
    q: "Pra cada nova instalação, preciso recomprar os adicionais?",
    a: "A princípio sim, mas isso é negociável. Fala com a gente que combinamos conforme o seu caso.",
  },
  {
    q: "Consigo rodar no celular?",
    a: "No momento, não. O Verdana funciona apenas em computadores ou notebooks com Windows 10 ou 11.",
  },
  {
    q: "É cobrado algum valor pra fazer manutenção?",
    a: "Com o plano de manutenção (R$19/mês), o suporte/visitas técnicas estão incluídos. Sem o plano, atendimentos pontuais por WhatsApp são avaliados caso a caso, e visitas técnicas custam R$50.",
  },
  {
    q: "O aplicativo emite notas fiscais (NFE, NFSE, NFCE)?",
    a: "Ainda não, mas já está na nossa lista de funcionalidades planejadas pra lançar no futuro.",
  },
  {
    q: "Encontrei um bug, e agora?",
    a: "Manda mensagem pelo WhatsApp. Assinantes da manutenção têm correção prioritária; sem o plano, a correção entra conforme a fila de atualizações.",
  },
];

export function Contact() {
  return (
    <Box
      component="section"
      id="faq"
      pt={{ base: 120, md: 180 }}
      pb={{ base: 80, md: 120 }}
      pos="relative"
      style={{
        background:
          "linear-gradient(160deg, var(--mantine-color-green-7) 0%, var(--mantine-color-green-6) 45%, var(--mantine-color-green-8) 100%)",
        overflow: "hidden",
      }}
    >
      {/* Slant decorativo no topo — herda a cor de fundo da seção anterior */}
      <Box
        pos="absolute"
        top={0}
        left={0}
        right={0}
        h={{ base: 60, md: 100 }}
        style={{ zIndex: 2, pointerEvents: "none", lineHeight: 0 }}
      >
        <svg
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
          style={{ width: "100%", height: "100%", display: "block" }}
          aria-hidden="true"
        >
          <title>Onda decorativa</title>
          <polygon points="0,0 1440,0 1440,100" fill="white" />
        </svg>
      </Box>
      <Box
        pos="absolute"
        top={-180}
        right={-160}
        w={460}
        h={460}
        bg="green.4"
        style={{ borderRadius: "50%", filter: "blur(120px)", opacity: 0.55 }}
      />
      <Box
        pos="absolute"
        bottom={-180}
        left={-160}
        w={420}
        h={420}
        bg="green.9"
        style={{ borderRadius: "50%", filter: "blur(140px)", opacity: 0.6 }}
      />
      <Box
        pos="absolute"
        top="40%"
        left="50%"
        w={300}
        h={300}
        bg="orange.4"
        style={{
          borderRadius: "50%",
          filter: "blur(140px)",
          opacity: 0.12,
          transform: "translate(-50%, -50%)",
        }}
      />

      <Container size="lg" pos="relative" style={{ zIndex: 3 }}>
        <Stack gap="sm" maw={960} mx="auto" mb={56}>
          <Title
            order={2}
            c="white"
            fz={{ base: 32, md: 48 }}
            fw={800}
            lh={1.08}
            style={{ letterSpacing: "-0.03em" }}
          >
            Dúvidas frequentes.
          </Title>
          <Text fz={{ base: 16, md: 18 }} lh={1.55} style={{ color: "rgba(255,255,255,0.85)" }}>
            As perguntas que mais aparecem por aqui. Se a sua não estiver na lista, é só chamar a
            gente no WhatsApp.
          </Text>
        </Stack>

        <Accordion
          variant="separated"
          radius="lg"
          maw={960}
          mx="auto"
          chevronPosition="right"
          styles={{
            root: {
              "--accordion-chevron-size": "28px",
            } as React.CSSProperties,
            item: {
              backgroundColor: "white",
              border: "1px solid rgba(255,255,255,0.4)",
              boxShadow:
                "0 10px 30px -12px rgba(6, 78, 59, 0.35), 0 2px 6px -2px rgba(6, 78, 59, 0.15)",
              transition: "transform 200ms ease, box-shadow 200ms ease",
            },
            control: {
              paddingTop: 20,
              paddingBottom: 20,
              paddingLeft: 24,
              paddingRight: 20,
            },
            label: {
              fontWeight: 600,
              fontSize: 16,
              color: "var(--mantine-color-dark-9)",
              lineHeight: 1.35,
            },
            content: {
              color: "var(--mantine-color-gray-7)",
              lineHeight: 1.65,
              fontSize: 15,
              paddingTop: 0,
              paddingBottom: 22,
              paddingLeft: 24,
              paddingRight: 24,
            },
            chevron: {
              color: "var(--mantine-color-green-7)",
              backgroundColor: "var(--mantine-color-green-0)",
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          }}
        >
          {faqs.map((f, idx) => (
            <Accordion.Item key={f.q} value={`faq-${idx}`}>
              <Accordion.Control>{f.q}</Accordion.Control>
              <Accordion.Panel>{f.a}</Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>

        <Box
          mt={72}
          maw={960}
          mx="auto"
          p={{ base: 32, md: 48 }}
          pos="relative"
          style={{
            borderRadius: 24,
            overflow: "hidden",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)",
            backdropFilter: "blur(20px) saturate(160%)",
            WebkitBackdropFilter: "blur(20px) saturate(160%)",
            border: "1px solid rgba(255,255,255,0.9)",
            boxShadow: "0 20px 60px -20px rgba(20, 83, 45, 0.4), inset 0 1px 0 rgba(255,255,255,1)",
          }}
        >
          <Container size="lg" p={0} pos="relative">
            <Group justify="space-between" align="center" gap="xl" wrap="wrap">
              <Stack gap="xs" maw={560}>
                <Title
                  order={3}
                  c="dark.9"
                  fz={{ base: 26, md: 36 }}
                  fw={800}
                  lh={1.15}
                  style={{ letterSpacing: "-0.02em" }}
                >
                  Ainda na dúvida?
                  <br />
                  <Text
                    component="span"
                    inherit
                    variant="gradient"
                    gradient={{ from: "green.7", to: "green.5", deg: 135 }}
                  >
                    Pergunta direto pra gente.
                  </Text>
                </Title>
                <Text c="gray.7" fz={{ base: 15, md: 16 }} mt={4}>
                  Respondemos em horário comercial. Sem bot, sem formulário, sem te empurrar pra um
                  vendedor.
                </Text>
              </Stack>

              <Button
                size="xl"
                variant="gradient"
                gradient={{ from: "green.7", to: "green.5", deg: 135 }}
                leftSection={<IconBrandWhatsapp size={22} />}
                component="a"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener"
                radius="xl"
                styles={{
                  root: {
                    fontWeight: 700,
                    boxShadow: "0 12px 32px -8px var(--mantine-color-green-4)",
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                  },
                }}
                className="contact-cta"
              >
                Abrir WhatsApp
              </Button>
            </Group>
          </Container>
        </Box>
      </Container>
    </Box>
  );
}
